import React from "react";

import { api } from "src/common/utils/api";
import {
	useCreateGenericError,
	useCreateMessage
} from "src/features/notifications";
import { useOrgModParams } from "src/features/orgmod";
import { messages } from "src/views/user-list/messages";
import { RawUser, User } from "src/views/user-list/types";
import { useNormalizeUser } from "src/views/user-list/useNormalizeUser";

interface UserListState {
	isLoading: boolean;
	error: unknown | null;
	users: UserItemState[];
}

interface UserItemState extends User {
	isLoading: boolean;
	error: unknown | null;
}

type UserListAction =
	| FetchStartAction
	| FetchSuccessAction
	| FetchFailureAction
	| ActivateStartAction
	| ActivateSuccessAction
	| ActivateFailureAction
	| DeactivateStartAction
	| DeactivateSuccessAction
	| DeactivateFailureAction;

interface FetchStartAction {
	type: "FETCH_START";
}

interface FetchSuccessAction {
	type: "FETCH_SUCCESS";
	payload: { users: User[] };
}

interface FetchFailureAction {
	type: "FETCH_FAILURE";
	payload: { error: unknown };
}

interface ActivateStartAction {
	type: "ACTIVATE_START";
	payload: { id: string };
}

interface ActivateSuccessAction {
	type: "ACTIVATE_SUCCESS";
	payload: { id: string };
}

interface ActivateFailureAction {
	type: "ACTIVATE_FAILURE";
	payload: { id: string; error: unknown };
}

interface DeactivateStartAction {
	type: "DEACTIVATE_START";
	payload: { id: string };
}

interface DeactivateSuccessAction {
	type: "DEACTIVATE_SUCCESS";
	payload: { id: string };
}

interface DeactivateFailureAction {
	type: "DEACTIVATE_FAILURE";
	payload: { id: string; error: unknown };
}

const initialState: UserListState = {
	isLoading: false,
	error: null,
	users: []
};

export function useUserList(): UserListState & {
	activateUser(id: string): void;
	deactivateUser(id: string): void;
} {
	const [state, dispatch] = React.useReducer(reducer, initialState);

	const normalize = useNormalizeUser();
	const createMessage = useCreateMessage();
	const createGenericError = useCreateGenericError();

	const { org, mod } = useOrgModParams();

	const fetchUsers = React.useCallback(async () => {
		dispatch({ type: "FETCH_START" });
		try {
			const { data } = await api.get<RawUser[]>(
				`/users?organization=${org}&module=${mod}`
			);
			dispatch({
				type: "FETCH_SUCCESS",
				payload: { users: data.map(normalize) }
			});
		} catch (error) {
			dispatch({ type: "FETCH_FAILURE", payload: { error } });
			createGenericError();
		}
	}, [normalize, createGenericError, org, mod]);

	React.useEffect(() => {
		fetchUsers();
	}, [fetchUsers]);

	const activateUser = React.useCallback(
		async (id: string) => {
			dispatch({ type: "ACTIVATE_START", payload: { id } });
			try {
				await api.patch(
					`/users/${id}?organization=${org}&module=${mod}`,
					{ op: "activate" }
				);
				dispatch({ type: "ACTIVATE_SUCCESS", payload: { id } });
				createMessage(messages.activateSuccess);
			} catch (error) {
				dispatch({ type: "ACTIVATE_FAILURE", payload: { id, error } });
				createGenericError();
			}
		},
		[createMessage, createGenericError, org, mod]
	);

	const deactivateUser = React.useCallback(
		async (id: string) => {
			dispatch({ type: "DEACTIVATE_START", payload: { id } });
			try {
				await api.patch(
					`/users/${id}?organization=${org}&module=${mod}`,
					{ op: "deactivate" }
				);
				dispatch({ type: "DEACTIVATE_SUCCESS", payload: { id } });
				createMessage(messages.deactivateSuccess);
			} catch (error) {
				dispatch({
					type: "DEACTIVATE_FAILURE",
					payload: { id, error }
				});
				createGenericError();
			}
		},
		[createMessage, createGenericError, org, mod]
	);

	return {
		...state,
		activateUser,
		deactivateUser
	};
}

function reducer(state: UserListState, action: UserListAction): UserListState {
	if (action.type === "FETCH_START") {
		return handleFetchStart(state);
	}
	if (action.type === "FETCH_SUCCESS") {
		return handleFetchSuccess(state, action);
	}
	if (action.type === "FETCH_FAILURE") {
		return handleFetchFailure(state, action);
	}
	if (
		action.type === "ACTIVATE_START" ||
		action.type === "DEACTIVATE_START"
	) {
		return handleItemStart(state, action);
	}
	if (action.type === "ACTIVATE_SUCCESS") {
		return handleActivateSuccess(state, action);
	}
	if (action.type === "DEACTIVATE_SUCCESS") {
		return handleDeactivateSuccess(state, action);
	}
	if (
		action.type === "ACTIVATE_FAILURE" ||
		action.type === "DEACTIVATE_FAILURE"
	) {
		return handleItemFailure(state, action);
	}
	return state;
}

function handleFetchStart(state: UserListState): UserListState {
	return {
		...state,
		isLoading: true,
		error: null,
		users: []
	};
}

function handleFetchSuccess(
	state: UserListState,
	{ payload: { users } }: FetchSuccessAction
): UserListState {
	return {
		...state,
		isLoading: false,
		users: users.map(user => ({
			...user,
			isLoading: false,
			error: null
		}))
	};
}

function handleFetchFailure(
	state: UserListState,
	{ payload: { error } }: FetchFailureAction
): UserListState {
	return {
		...state,
		isLoading: false,
		error
	};
}

function handleItemStart(
	state: UserListState,
	{ payload: { id } }: ActivateStartAction | DeactivateStartAction
): UserListState {
	return {
		...state,
		users: state.users.map(user =>
			user.id === id
				? {
						...user,
						isLoading: true,
						error: null
					}
				: user
		)
	};
}

function handleActivateSuccess(
	state: UserListState,
	{ payload: { id } }: ActivateSuccessAction
): UserListState {
	return {
		...state,
		users: state.users.map(user =>
			user.id === id
				? {
						...user,
						isLoading: false,
						inactive: false
					}
				: user
		)
	};
}

function handleDeactivateSuccess(
	state: UserListState,
	{ payload: { id } }: DeactivateSuccessAction
): UserListState {
	return {
		...state,
		users: state.users.map(user =>
			user.id === id
				? {
						...user,
						isLoading: false,
						inactive: true
					}
				: user
		)
	};
}

function handleItemFailure(
	state: UserListState,
	{ payload: { id, error } }: ActivateFailureAction | DeactivateFailureAction
): UserListState {
	return {
		...state,
		users: state.users.map(user =>
			user.id === id
				? {
						...user,
						isLoading: false,
						error
					}
				: user
		)
	};
}
